import cx from 'classnames';
import { ChevronDown } from 'ethena-icons';
import _ from 'lodash';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import css from './ShowMore.module.scss';

interface IShowMoreContext {
  isExpanded: boolean;
  toggle: () => void;
}

const ShowMoreContext = createContext<IShowMoreContext>({
  isExpanded: true,
  toggle: _.noop,
});

const useShowMoreContext = (): IShowMoreContext => useContext(ShowMoreContext);

interface IShowMoreProps {
  children: ReactNode;
  isDefaultExpanded?: boolean;
  isExpanded?: boolean;
  toggle?: () => void;
}

const ShowMore = ({
  children,
  isDefaultExpanded = true,
  isExpanded: isExpandedOverride,
  toggle: toggleOverride,
}: IShowMoreProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isDefaultExpanded);
  const toggle = () => setIsExpanded(!isExpanded);

  useEffect(() => {
    if (_.isBoolean(isExpandedOverride)) {
      setIsExpanded(isExpandedOverride);
    }
  }, [isExpandedOverride]);

  return (
    <ShowMoreContext.Provider
      value={{
        isExpanded,
        toggle: toggleOverride || toggle,
      }}
    >
      {children}
    </ShowMoreContext.Provider>
  );
};

interface IToggleProps {
  children: ReactNode;
  className?: string;
  collapsedLabel?: string;
  expandedClassName?: string;
  expandedLabel?: string;
  arrowClassName?: string;
}

const Toggle = ({
  children,
  className,
  collapsedLabel,
  expandedClassName,
  expandedLabel,
  arrowClassName,
}: IToggleProps) => {
  const { isExpanded, toggle } = useShowMoreContext();
  return (
    <button
      aria-expanded={isExpanded}
      aria-label={isExpanded ? expandedLabel : collapsedLabel}
      className={cx(css.toggle, className, {
        [css.expandedToggle]: isExpanded,
        [expandedClassName]: isExpanded,
      })}
      onClick={toggle}
    >
      {children}
      <div>
        <ChevronDown className={cx(css.arrow, arrowClassName)} />
      </div>
    </button>
  );
};

interface IContentProps {
  children: ReactNode;
}

const Content = ({ children }: IContentProps) => {
  const { isExpanded } = useShowMoreContext();
  return isExpanded && <>{children}</>;
};

interface IToggleLabelProps {
  children: ReactNode;
  isCollapsedLabel?: boolean;
  isExpandedLabel?: boolean;
}

const ToggleLabel = ({
  children,
  isCollapsedLabel = false,
  isExpandedLabel = false,
}: IToggleLabelProps) => {
  const { isExpanded } = useShowMoreContext();
  if (!isCollapsedLabel && !isExpandedLabel) {
    return <>{children}</>;
  } else if (isExpanded && isExpandedLabel) {
    return <>{children}</>;
  } else if (!isExpanded && isCollapsedLabel) {
    return <>{children}</>;
  } else {
    return null;
  }
};

Toggle.Label = ToggleLabel;
ShowMore.Content = Content;
ShowMore.Toggle = Toggle;

export { ShowMore };
